import { useEffect, useRef } from "react";
import EditorJS from "@editorjs/editorjs";
import Header from "@editorjs/header";
import List from "@editorjs/list";
import ImageTool from "@editorjs/image";
import Quote from "@editorjs/quote";
import Embed from "@editorjs/embed";
import Table from "@editorjs/table";
import Marker from "@editorjs/marker";
import InlineCode from "@editorjs/inline-code";
import { serverPath } from "config/config";
import { ruLocale } from "components/common/editor/editorlocale.js";
import ExtendedImageTool from "./ExtendedImageTool";
import BookmarkTool from "./BookmarkTool";
import "./Editor.module.css";

const YOUTUBE_URL_REGEX =
  /^https?:\/\/(?:www\.)?(?:youtube\.com|youtu\.be)\/(?:watch\?v=|embed\/|v\/|shorts\/)?[a-zA-Z0-9_-]+/i;
const VK_URL_REGEX =
  /^https?:\/\/(?:www\.)?(?:vk\.com|vkvideo\.ru)\/(?:video[-_]?|video\/|clip[-_]?|clip\/|video_ext\.php\?.*?)-?\d+_\d+/i;

function stripHtml(value = "") {
  return String(value)
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/&nbsp;/gi, " ")
    .replace(/<[^>]*>/g, "")
    .trim();
}

function resolveEmbedService(url = "") {
  if (YOUTUBE_URL_REGEX.test(url)) return "youtube";
  if (VK_URL_REGEX.test(url)) return "vk";
  return null;
}

const Editor = ({
  data,
  onChange,
  holder = "editorjs-container",
  readOnly,
  viewMode = "edit",
  config = {},
}) => {
  const editorRef = useRef(null);
  const editorContainerRef = useRef(null);
  const isAutoConvertingEmbedRef = useRef(false);

  useEffect(() => {
    if (!editorRef.current && editorContainerRef.current) {
      const initEditor = async () => {
        const editorData = data ? JSON.parse(JSON.stringify(data)) : { blocks: [] };

        editorRef.current = new EditorJS({
          holder: editorContainerRef.current,
          readOnly: readOnly,
          tools: {
            header: {
              class: Header,
              config: {
                placeholder: "Введите заголовок",
                levels: [1, 2, 3],
                defaultLevel: 1,
              },
            },
            list: {
              class: List,
              inlineToolbar: true,
              config: {
                defaultStyle: "unordered",
                types: {
                  unordered: "Маркированный список",
                  ordered: "Нумерованный список",
                },
                meta: {
                  counterType: "numeric",
                },
              },
            },
            // Используем расширенный инструмент изображений
            image: {
              class: ExtendedImageTool,
              config: {
                endpoints: {
                  byFile: `${serverPath}/scripts/upload.php`,
                },
                stretch: true,
                styles: {
                  stretched: {
                    name: "stretched",
                    className: "image-tool--stretched",
                  },
                },
                caption: "Введите описание изображения (alt текст)",
                additionalRequestHeaders: {
                  "X-CSRF-Token": "...",
                },
                field: "image",
                types: "image/*",
                additionalRequestData: {
                  alt: "Описание изображения по умолчанию",
                },
              },
            },
            embed: {
              class: Embed,
              config: {
                services: {
                  youtube: {
                    regex:
                      /https?:\/\/(?:www\.)?(?:youtube\.com|youtu\.be)\/(?:watch\?v=|embed\/|v\/|shorts\/)?([a-zA-Z0-9_-]+)/,
                    embedUrl: "https://www.youtube.com/embed/<%= remote_id %>",
                    html: "<iframe width='100%' height='auto' frameborder='0' allowfullscreen></iframe>",
                    height: 400,
                    width: "100%",
                  },
                  vk: {
                    regex:
                      /(?:https?:\/\/)?(?:www\.)?(?:vk\.com|vkvideo\.ru)\/(?:video[-_]?|video\/|clip[-_]?|clip\/|video_ext\.php\?.*?)(-?\d+_\d+)/i,
                    embedUrl: "https://vk.com/video_ext.php?<%= remote_id %>&hd=2",
                    html: "<iframe height='400px' scrolling='no' frameborder='no' allowtransparency='true' allowfullscreen='true' style='width: 100%;'></iframe>",
                    id: (groups) => {
                      const raw = groups?.[0] || "";
                      const [ownerRaw, id] = raw.split("_");
                      if (!ownerRaw || !id) return null;

                      const ownerId = ownerRaw.startsWith("-")
                        ? ownerRaw
                        : `-${ownerRaw}`;
                      return `oid=${ownerId}&id=${id}`;
                    },
                  },
                },
                caption: false,
              },
            },
            table: Table,
            marker: Marker,
            inlineCode: InlineCode,
            bookmark: {
              class: BookmarkTool,
              inlineToolbar: false,
              config: {
                viewMode: viewMode,
                serverPath: config.serverPath || serverPath,
                articleHash: config.articleHash,
              },
            },
          },
          data: editorData,
          onReady: () => {
            document.querySelectorAll(".ce-block").forEach((block) => {
              block.style.maxWidth = "none";
            });

            if (readOnly) {
              document
                .querySelectorAll(".ce-toolbar, .ce-toolbar__plus, .ce-toolbar__settings-btn")
                .forEach((block) => (block.style.display = "none"));
            }

            setTimeout(() => {
              document.querySelectorAll(".image-tool").forEach((imgBlock) => {
                imgBlock.dispatchEvent(new Event("load"));
              });
            }, 500);
          },
          onChange: async (api) => {
            if (isAutoConvertingEmbedRef.current) {
              isAutoConvertingEmbedRef.current = false;
              const content = await api.saver.save();
              onChange(content);
              return;
            }

            const content = await api.saver.save();
            const currentBlockIndex = api.blocks.getCurrentBlockIndex();
            const currentBlock = content?.blocks?.[currentBlockIndex];
            const paragraphText = stripHtml(currentBlock?.data?.text || "");
            const embedService =
              currentBlock?.type === "paragraph"
                ? resolveEmbedService(paragraphText)
                : null;

            if (!readOnly && embedService && paragraphText) {
              isAutoConvertingEmbedRef.current = true;
              api.blocks.delete(currentBlockIndex);
              api.blocks.insert(
                "embed",
                {
                  service: embedService,
                  source: paragraphText,
                },
                undefined,
                currentBlockIndex,
                true
              );

              const updatedContent = await api.saver.save();
              onChange(updatedContent);
              return;
            }

            onChange(content);
          },
          placeholder: "Начните вводить текст...",
          i18n: ruLocale,
          minHeight: 100,
        });
      };

      initEditor().catch(console.error);
    }

    return () => {};
  }, [data, holder, onChange, readOnly, viewMode, config]);

  return (
    <div
      ref={editorContainerRef}
      className="editorjs-wrapper"
    />
  );
};

export default Editor;
